import { useEffect, useState } from 'react'
import { displayName, PROFILE_TABS } from '../data/mockClients'
import type { ClientProfile } from '../data/types'
import { ApiError } from '../api/auth'
import { getClient } from '../api/clients'
import { archiveClient } from '../api/clients'
import { navigate } from '../router/hash'
import { AppShell } from '../layout/AppShell'
import { Avatar } from '../components/Avatar'
import { Icon } from '../components/Icon'
import { statusBadgeClass } from '../data/nav'
import { TAB_TITLE, isProfileTab } from '../data/profileTabs'
import { FaceSheetTab } from './ClientProfile/FaceSheetTab'
import { ClinicalTab, hasNativeSection } from './ClientProfile/ClinicalTab'
import { listClientAuditEvents, listClientRecords, type ClientAuditEvent, type ClientRecord } from '../api/clientRecords'

type Props = {
  clientId: string
  tabId: string
  currentPath: string
}

const OVERVIEW = 'overview'

export function ClientDetailPage({ clientId, tabId, currentPath }: Props) {
  const [client, setClient] = useState<ClientProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError('')
    getClient(clientId)
      .then((value) => {
        if (mounted) setClient(value)
      })
      .catch((reason) => {
        if (mounted) setError(reason instanceof ApiError ? reason.message : 'Unable to load this client.')
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [clientId])

  if (loading) {
    return (
      <AppShell currentPath={currentPath} title="Client profile">
        <div className="container"><div className="empty card"><p>Loading client…</p></div></div>
      </AppShell>
    )
  }

  if (!client) {
    return (
      <AppShell currentPath={currentPath} title="Client not found">
        <div className="container">
          <div className="empty card" style={{ padding: '3rem' }}>
            <p>{error || `We couldn't find a client with ID ${clientId}.`}</p>
            <button type="button" className="btn btn--primary" onClick={() => navigate('/clients')}>
              Back to clients
            </button>
          </div>
        </div>
      </AppShell>
    )
  }

  const name = displayName(client)
  const activeTab = tabId || OVERVIEW
  const isOverview = activeTab === OVERVIEW

  const go = (tab: string) => navigate(`/clients/${clientId}/${tab}`)
  const archive = async () => {
    if (!window.confirm(`Archive ${name}? The clinical history will be retained.`)) return
    try {
      await archiveClient(clientId)
      navigate('/clients')
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to archive this client.')
    }
  }

  return (
    <AppShell currentPath={currentPath} title="Client profile" subtitle={name}>
      <div className="container">
        {/* Hero */}
        <div className="detail-hero">
          <Avatar client={client} size="xl" />
          <div className="detail-hero__info">
            <div className="detail-hero__name">
              <h1>{name}</h1>
              <span className={statusBadgeClass(client.status)}>{client.status}</span>
            </div>
            <div className="detail-hero__meta">
              <span>
                <Icon name="id" /> ID {client.id}
              </span>
              <span>
                <Icon name="phone" /> {client.phoneCell || client.phoneHome || '—'}
              </span>
              <span>
                <Icon name="calendar" /> DOB {client.dob || '—'}
              </span>
            </div>
          </div>
          <div className="detail-hero__actions">
            <button type="button" className="btn btn--primary btn--sm" onClick={() => navigate(`/clients/${clientId}/edit`)}>
              <Icon name="edit" /> Edit
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => go('dashboard')}>
              <Icon name="chart" /> Dashboard
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => go('audit')}>
              <Icon name="audit" /> Audit
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => window.print()}>
              Print
            </button>
            <button type="button" className="btn btn--subtle btn--sm" onClick={() => navigate('/clients')}>
              <Icon name="chevron-left" /> Roster
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => void archive()}>
              Archive
            </button>
          </div>
        </div>

        {/* Section navigation */}
        <div className="pane-nav" style={{ marginTop: '1.25rem' }}>
          <a
            className={isOverview ? 'is-active' : ''}
            onClick={(e) => {
              e.preventDefault()
              go(OVERVIEW)
            }}
          >
            Overview
          </a>
          {PROFILE_TABS.map((t) => (
            <a
              key={t.id}
              className={activeTab === t.id ? 'is-active' : ''}
              onClick={(e) => {
                e.preventDefault()
                go(t.id)
              }}
            >
              {t.label}
            </a>
          ))}
        </div>

        {isOverview ? (
          <Overview client={client} onOpen={go} />
        ) : activeTab === 'face-sheet' ? (
          <FaceSheetTab client={client} />
        ) : hasNativeSection(activeTab) ? (
          <ClinicalTab tabId={activeTab} client={client} />
        ) : activeTab === 'dashboard' ? (
          <ClientDashboard clientId={client.id} />
        ) : activeTab === 'audit' ? (
          <ClientAudit clientId={client.id} />
        ) : (
          <EmbeddedTab tabId={activeTab} />
        )}
      </div>
    </AppShell>
  )
}

function ClientDashboard({ clientId }: { clientId: string }) {
  const [records, setRecords] = useState<ClientRecord[]>([])
  const [error, setError] = useState('')

  useEffect(() => {
    listClientRecords(clientId).then(setRecords).catch(() => setError('Unable to load dashboard data.'))
  }, [clientId])

  const counts = records.reduce<Record<string, number>>((acc, record) => {
    acc[record.section] = (acc[record.section] || 0) + 1
    return acc
  }, {})
  const updatedAt = records[0]?.updatedAt

  return (
    <div className="detail-grid" style={{ marginTop: '1rem' }}>
      <div className="card">
        <div className="card__head"><h3>Chart summary</h3></div>
        <div className="card__body">
          {error ? <p className="muted">{error}</p> : (
            <div className="facts">
              <div className="fact"><span className="fact__k">Total records</span><span className="fact__v">{records.length}</span></div>
              <div className="fact"><span className="fact__k">Active sections</span><span className="fact__v">{Object.keys(counts).length}</span></div>
              <div className="fact"><span className="fact__k">Last updated</span><span className="fact__v">{updatedAt ? new Date(updatedAt).toLocaleString() : '—'}</span></div>
            </div>
          )}
        </div>
      </div>
      <div className="card">
        <div className="card__head"><h3>Records by section</h3></div>
        <div className="card__body">
          {Object.keys(counts).length ? <div className="tile-grid">
            {Object.entries(counts).map(([section, count]) => (
              <div className="tile" key={section}><div className="tile__ic"><Icon name="reports" /></div><b>{section}</b><span>{count} record{count === 1 ? '' : 's'}</span></div>
            ))}
          </div> : <p className="muted">No chart records have been added yet.</p>}
        </div>
      </div>
    </div>
  )
}

function ClientAudit({ clientId }: { clientId: string }) {
  const [events, setEvents] = useState<ClientAuditEvent[]>([])
  const [error, setError] = useState('')

  useEffect(() => {
    listClientAuditEvents(clientId).then(setEvents).catch(() => setError('Unable to load client audit history.'))
  }, [clientId])

  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <div className="card__head"><h3>Client audit history</h3><div style={{ flex: 1 }} /><span className="muted">{events.length} events</span></div>
      <div className="table-wrap">
        <table className="data-table">
          <thead><tr><th>Date &amp; time</th><th>Action</th><th>Section</th><th>Reference</th></tr></thead>
          <tbody>{events.map((event) => <tr key={event.id}>
            <td className="muted">{new Date(event.occurredAt).toLocaleString()}</td>
            <td>{event.action.replaceAll('_', ' ')}</td>
            <td className="muted">{event.entityType || '—'}</td>
            <td className="muted">{event.entityId || '—'}</td>
          </tr>)}</tbody>
        </table>
        {error || !events.length ? <div className="empty"><p>{error || 'No client-specific audit events yet.'}</p></div> : null}
      </div>
    </div>
  )
}

function Overview({
  client,
  onOpen,
}: {
  client: ClientProfile
  onOpen: (tab: string) => void
}) {
  const facts: [string, string][] = [
    ['Client ID', client.id],
    ['Status', client.status],
    ['Date of birth', client.dob || '—'],
    ['Cell phone', client.phoneCell || '—'],
    ['Home phone', client.phoneHome || '—'],
    ['Care manager', client.careManager || '—'],
    ['Program', client.program || '—'],
  ]

  return (
    <div className="detail-grid">
      <div className="card">
        <div className="card__head">
          <h3>Client details</h3>
        </div>
        <div className="card__body">
          <div className="facts">
            {facts.map(([k, v]) => (
              <div key={k} className="fact">
                <span className="fact__k">{k}</span>
                <span className="fact__v">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__head">
          <h3>Chart sections</h3>
          <div style={{ flex: '1' }} />
          <span className="muted" style={{ fontSize: '.8rem' }}>
            {PROFILE_TABS.length} sections
          </span>
        </div>
        <div className="card__body">
          <div className="tile-grid">
            {PROFILE_TABS.map((t) => (
              <a
                key={t.id}
                className="tile"
                onClick={(e) => {
                  e.preventDefault()
                  onOpen(t.id)
                }}
              >
                <div className="tile__ic">
                  <Icon name="reports" />
                </div>
                <b>{t.label}</b>
                <span>Open section</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function EmbeddedTab({ tabId }: { tabId: string }) {
  const label = isProfileTab(tabId) ? TAB_TITLE[tabId] : 'Section'

  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <div className="card__body" style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
        <div className="tile__ic" style={{ width: 56, height: 56, margin: '0 auto 1rem', borderRadius: 14 }}>
          <Icon name="reports" />
        </div>
        <h3 style={{ fontSize: '1.1rem', margin: 0 }}>{label}</h3>
        <p className="muted" style={{ maxWidth: '42ch', margin: '0.6rem auto 0' }}>
          This chart is available as a native React view. Open Overview or Face Sheet for the
          migrated sections — remaining reports use the same shell without HTML embeds.
        </p>
      </div>
    </div>
  )
}
